import { createContext, useContext, useMemo, useState } from 'react';
import type React from 'react';
import type { DeviceData, ScreenSize } from '~/contracts';

const getScreenSizeFromDeviceData = (deviceData: DeviceData): ScreenSize => ({
  height: deviceData.height,
  isDesktop: deviceData.isDesktop,
  isMobile: deviceData.isMobile,
  isTablet: deviceData.isTablet,
  width: deviceData.width
});

const defaultDeviceData: DeviceData = {
  browserName: '',
  deviceName: '',
  height: 0,
  isDesktop: false,
  isMobile: true,
  isTablet: false,
  width: 0
};

const defaultContext = {
  browserName: defaultDeviceData.browserName,
  deviceName: defaultDeviceData.deviceName,
  screenSize: getScreenSizeFromDeviceData(defaultDeviceData),
  setScreenSize: (_: ScreenSize) => {}
};

export type ContextType = typeof defaultContext;

const DeviceContext = createContext<ContextType>(defaultContext);

export type OwnProps = {
  deviceData?: DeviceData;
};

export const DeviceContextProvider = ({
  children,
  deviceData
}: React.PropsWithChildren<OwnProps>) => {
  const { browserName, deviceName } = deviceData ?? defaultDeviceData;
  const [screenSize, setScreenSize] = useState(
    getScreenSizeFromDeviceData(deviceData ?? defaultDeviceData)
  );

  const value = useMemo(
    () => ({ browserName, deviceName, screenSize, setScreenSize }),
    [browserName, deviceName, screenSize]
  );

  return (
    <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
  );
};

DeviceContextProvider.displayName = 'DeviceContextProvider';

export const useScreenSize = (): ScreenSize =>
  useContext(DeviceContext).screenSize;

export const useIsMobile = (): boolean => useScreenSize().isMobile;

export default DeviceContext;
